import FadeIn from '../components/FadeIn';
import { Link } from 'react-router-dom';

const IMAGES = {
  bowls: "https://customer-assets.emergentagent.com/job_70c97ebb-d9b4-4dac-8385-96529bba2f67/artifacts/60gea409_1000001871.jpg",
  indoor: "https://customer-assets.emergentagent.com/job_70c97ebb-d9b4-4dac-8385-96529bba2f67/artifacts/bdoytqmu_website%20photo%204.jpg",
  gong: "https://customer-assets.emergentagent.com/job_70c97ebb-d9b4-4dac-8385-96529bba2f67/artifacts/alpxg7xy_website%20photo%203.jpg",
  lotus: "https://customer-assets.emergentagent.com/job_70c97ebb-d9b4-4dac-8385-96529bba2f67/artifacts/d1t7n4w6_website%20photo%20one.png",
};

const PRINCIPLES = [
  {
    title: 'Safety First',
    desc: 'Every session begins with establishing genuine safety within your nervous system. Nothing is rushed. Your system leads.',
  },
  {
    title: 'Deep Attunement',
    desc: 'This work is guided by attunement—a genuine presence and resonance with where you are in each moment.',
  },
  {
    title: 'Trauma-Informed',
    desc: 'All work is approached with trauma-informed understanding, honoring the complexity of your system and history.',
  },
  {
    title: 'Sustainable Transformation',
    desc: 'Rather than forcing change, we allow genuine reorganization to unfold in a way that is sustainable and embodied.',
  },
  {
    title: 'Non-Pathologizing',
    desc: 'You are not broken or in need of fixing. Your patterns are intelligent responses. We work with them, not against them.',
  },
  {
    title: 'Integrity',
    desc: 'This work is conducted with honesty, transparency, and a deep respect for your autonomy and individual process.',
  },
];

const AboutPage = () => {
  return (
    <div data-testid="about-page" style={{ background: '#F7F4EF' }}>

      {/* Hero Banner */}
      <div className="pt-40 pb-24 text-center" style={{ background: '#EAE3D9' }}>
        <FadeIn>
          <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74' }}>
            The Practitioner
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl" style={{ color: '#2B2B2B' }}>
            About This Work
          </h1>
          <p
            className="text-base mt-6 max-w-2xl mx-auto px-6 leading-relaxed"
            style={{ color: 'rgba(43,43,43,0.68)', fontFamily: 'Inter, sans-serif' }}
          >
            A space rooted in safety, presence, and integrity—where your healing process is met with deep care.
          </p>
        </FadeIn>
      </div>

      {/* Intro Section */}
      <section data-testid="about-intro-section" className="py-24 md:py-32" style={{ background: '#F7F4EF' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div className="overflow-hidden rounded-3xl" style={{ boxShadow: '0 24px 64px rgba(126,140,116,0.14)' }}>
                <img
                  src={IMAGES.bowls}
                  alt="Sound healing with crystal bowls outdoors"
                  className="w-full object-cover img-hover"
                  style={{ height: '540px' }}
                />
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74' }}>
                A Grounded Space
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl mb-8" style={{ color: '#2B2B2B' }}>
                A Space Rooted in Safety, Presence, and Integrity
              </h2>
              <div className="space-y-5">
                {[
                  'This work is grounded in trauma-informed care, deep attunement, and respect for your individual process.',
                  'You are not rushed, pushed, or expected to be anywhere other than where you are. You are met there—with presence, care, and intention.',
                  'Every session, every container, every ceremony is held with deep reverence for where you are in your process. The pace is always yours.',
                  'This is a space that honors the intelligence of your nervous system and the wisdom of your individual healing journey.',
                ].map((para, i) => (
                  <p key={i} className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif' }}>
                    {para}
                  </p>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section data-testid="about-principles-section" className="py-24 md:py-32" style={{ background: '#EAE3D9' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74' }}>
                What This Work Stands For
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl" style={{ color: '#2B2B2B' }}>
                Core Principles
              </h2>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-6">
            {PRINCIPLES.map((principle, i) => (
              <FadeIn key={i} delay={i * 90}>
                <div
                  className="p-8 rounded-3xl h-full"
                  style={{ background: '#F7F4EF', boxShadow: '0 8px 40px rgba(126,140,116,0.07)' }}
                >
                  <div className="mb-4" style={{ width: '28px', height: '1px', background: '#C6A96B' }} />
                  <h3 className="font-serif text-xl mb-3" style={{ color: '#2B2B2B' }}>{principle.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'rgba(43,43,43,0.7)', fontFamily: 'Inter, sans-serif' }}>
                    {principle.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Method */}
      <section data-testid="about-method-section" className="py-24 md:py-32" style={{ background: '#F7F4EF' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <FadeIn delay={200}>
              <p className="text-xs tracking-[0.3em] uppercase font-medium mb-4" style={{ color: '#7E8C74' }}>
                The Approach
              </p>
              <h2 className="font-serif text-3xl sm:text-4xl mb-8" style={{ color: '#2B2B2B' }}>
                The Resonant Regulation Method™
              </h2>
              <div className="space-y-5 mb-10">
                {[
                  'The Resonant Regulation Method™ is a framework born from years of deep exploration at the intersection of somatic healing, subconscious work, and energetic alignment.',
                  'It is not a rigid protocol. It is a living, responsive approach that honors the organic nature of healing and the individuality of each person\'s process.',
                  'Through four interconnected phases—Regulate, Reveal, Rewire, Reintegrate—this method supports lasting, embodied transformation grounded in nervous system safety.',
                ].map((para, i) => (
                  <p key={i} className="text-base leading-relaxed" style={{ color: 'rgba(43,43,43,0.76)', fontFamily: 'Inter, sans-serif' }}>
                    {para}
                  </p>
                ))}
              </div>
              <a
                href="https://forms.gle/acnQmapg39FqW7wL7"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-apply-btn"
                className="inline-block rounded-full text-sm font-medium transition-all duration-300 hover:opacity-85"
                style={{ background: '#A8B5A2', color: '#2B2B2B', padding: '14px 34px', fontFamily: 'Inter, sans-serif' }}
              >
                Apply to Work Together
              </a>
            </FadeIn>
            <FadeIn>
              <div className="overflow-hidden rounded-3xl" style={{ boxShadow: '0 24px 64px rgba(126,140,116,0.14)' }}>
                <img
                  src={IMAGES.indoor}
                  alt="Indoor healing session"
                  className="w-full object-cover img-hover"
                  style={{ height: '520px' }}
                />
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Quote CTA */}
      <section data-testid="about-cta-section" className="py-24 text-center" style={{ background: '#7E8C74' }}>
        <div className="max-w-3xl mx-auto px-6">
          <FadeIn>
            <div className="mb-3" style={{ width: '1px', height: '48px', background: 'rgba(247,244,239,0.3)', margin: '0 auto 24px' }} />
            <p
              className="font-serif text-2xl sm:text-3xl leading-relaxed mb-12"
              style={{ color: '#F7F4EF', fontStyle: 'italic' }}
            >
              "You are not rushed, pushed, or expected to be anywhere other than where you are. You are met there."
            </p>
            <a
              href="https://forms.gle/acnQmapg39FqW7wL7"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta-btn"
              className="inline-block rounded-full text-sm font-medium transition-all duration-300 hover:opacity-85"
              style={{ background: '#C6A96B', color: '#2B2B2B', padding: '16px 42px', fontFamily: 'Inter, sans-serif' }}
            >
              Apply to Work Together
            </a>
          </FadeIn>
        </div>
      </section>

    </div>
  );
};

export default AboutPage;
